import type { ComponentProps } from 'react'

import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import { isSentryUserFeedbackAvailable } from '@/services/sentry'

export interface BetaBadgeProps {
	className?: string
	label?: string
	size?: 'sm' | 'md'
	side?: ComponentProps<typeof TooltipContent>['side']
	sideOffset?: number
}

/** Outline Beta label; tooltip mentions reporting feedback when Sentry feedback is on. */
export function BetaBadge({
	className,
	label,
	size = 'sm',
	side = 'top',
	sideOffset = 6,
}: BetaBadgeProps) {
	const { t } = useTranslation()
	const feedbackAvailable = isSentryUserFeedbackAvailable()
	const displayLabel = label ?? t('common.betaBadge.label')

	const tooltipSecondary = feedbackAvailable
		? t('common.betaBadge.tooltipFeedbackHint', { reportBug: t('navigation.reportBug') })
		: t('common.betaBadge.tooltipFeedbackFallback')

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="inline-flex max-w-full shrink-0 cursor-default">
					<Badge
						variant="outline"
						className={cn(
							size === 'sm' && 'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground',
							size === 'md' && 'text-xs font-medium text-muted-foreground',
							className
						)}
					>
						{displayLabel}
					</Badge>
				</span>
			</TooltipTrigger>
			<TooltipContent
				side={side}
				sideOffset={sideOffset}
				className="max-w-[min(280px,calc(100vw-2rem))] space-y-2 px-3 py-2 text-xs leading-snug"
			>
				<p className="font-medium">{t('common.betaBadge.tooltipIntro')}</p>
				<p className="opacity-95">{tooltipSecondary}</p>
			</TooltipContent>
		</Tooltip>
	)
}
